import { useEffect, useState } from 'react';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuthenticatedFetch } from '@/hooks/useAuthenticatedFetch';
import type { NotificationDetail } from '@/types/admin';

interface EmailViewDialogProps {
  userId: string;
  eventId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EmailViewDialog({
  userId,
  eventId,
  open,
  onOpenChange,
}: EmailViewDialogProps) {
  const authenticatedFetch = useAuthenticatedFetch();
  const [notification, setNotification] = useState<NotificationDetail | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('html');

  useEffect(() => {
    if (!open || !userId || !eventId) {
      setNotification(null);
      setError(null);
      return;
    }

    async function fetchNotification() {
      try {
        setLoading(true);
        setError(null);

        const response = await authenticatedFetch(
          `users/${userId}/notifications/${eventId}`,
          {
            method: 'GET',
          },
        );

        if (response.status === 204) {
          setError('Notification not found');
          setNotification(null);
          return;
        }

        if (!response.ok) {
          const contentType = response.headers.get('content-type');
          const body = contentType?.includes('application/json')
            ? await response.json().catch(() => ({}))
            : {};
          const message =
            typeof body?.error === 'string'
              ? body.error
              : `Failed to fetch notification (${response.status})`;
          throw new Error(message);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType?.includes('application/json')) {
          throw new Error('Invalid response format: expected JSON');
        }

        const data = (await response.json()) as NotificationDetail;
        setNotification(data);
      } catch (fetchError) {
        console.error(fetchError);
        setError(
          fetchError instanceof Error
            ? fetchError.message
            : 'Failed to load notification',
        );
        setNotification(null);
      } finally {
        setLoading(false);
      }
    }

    fetchNotification();
  }, [authenticatedFetch, userId, eventId, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader>
          <DialogTitle>Email Details</DialogTitle>
        </DialogHeader>
        <DialogClose onClose={() => onOpenChange(false)} />

        <div className="px-3 pb-3 sm:px-6 sm:pb-6">
          {loading && (
            <div className="py-8 text-center text-muted-foreground">
              Loading email...
            </div>
          )}

          {error && (
            <div className="py-8 text-center text-destructive">{error}</div>
          )}

          {!loading && !error && notification && (
            <div className="space-y-3 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">To</Label>
                  <div className="text-sm">{notification.destination}</div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">CC</Label>
                  <div className="text-sm">
                    {notification.ccAddresses &&
                    notification.ccAddresses.length > 0
                      ? notification.ccAddresses.join(', ')
                      : '—'}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    Subject
                  </Label>
                  <div className="text-sm font-medium">
                    {notification.subject}
                  </div>
                </div>
              </div>

              <div className="border-t pt-3 sm:pt-6">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList>
                    <TabsTrigger value="html">HTML View</TabsTrigger>
                    <TabsTrigger value="text">Plain Text</TabsTrigger>
                  </TabsList>

                  <TabsContent value="html" className="mt-3 sm:mt-6">
                    <div
                      className="rounded-md border bg-muted/30 overflow-auto"
                      style={{
                        minHeight: '300px',
                        maxHeight: '500px',
                        padding: '24px',
                      }}
                    >
                      <div
                        className="email-content"
                        dangerouslySetInnerHTML={{
                          __html: notification.htmlContent,
                        }}
                        style={{
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                          fontSize: '15px',
                          lineHeight: '1.6',
                          color: 'inherit',
                          maxWidth: '100%',
                        }}
                      />
                      <style>{`
                      .email-content * {
                        margin: 0;
                        padding: 0;
                        border: 0;
                        font-size: inherit;
                        font-family: inherit;
                        vertical-align: baseline;
                        box-sizing: border-box;
                      }
                      .email-content p {
                        margin-bottom: 1em;
                      }
                      .email-content br {
                        display: block;
                        content: "";
                        margin-top: 0.5em;
                      }
                    `}</style>
                    </div>
                  </TabsContent>

                  <TabsContent value="text" className="mt-3 sm:mt-6">
                    <pre
                      className="rounded-md border bg-muted/30 overflow-auto font-mono whitespace-pre-wrap break-words"
                      style={{
                        minHeight: '300px',
                        maxHeight: '500px',
                        padding: '24px',
                        fontSize: '13px',
                        lineHeight: '1.5',
                      }}
                    >
                      {notification.htmlContent}
                    </pre>
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
